/**
 * Impulse Response Loader
 * Fetches and decodes audio files for convolution reverb
 */

import { hashString } from './bytecode.ts'

export interface ImpulseResponseData {
  id: number
  path: string
  audioBuffer: AudioBuffer
}

export class ImpulseResponseLoader {
  private audioContext: AudioContext
  private loadedIRs = new Map<number, ImpulseResponseData>()
  private loadingPromises = new Map<number, Promise<ImpulseResponseData>>()

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext
  }

  async loadImpulseResponse(path: string): Promise<ImpulseResponseData> {
    const id = hashString(path)

    // Return cached IR if already loaded
    if (this.loadedIRs.has(id)) {
      return this.loadedIRs.get(id)!
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(id)) {
      return this.loadingPromises.get(id)!
    }

    // Start loading
    const promise = this.fetchAndDecodeIR(path, id)
    this.loadingPromises.set(id, promise)

    try {
      const irData = await promise
      this.loadedIRs.set(id, irData)
      this.loadingPromises.delete(id)
      return irData
    } catch (error) {
      this.loadingPromises.delete(id)
      throw error
    }
  }

  private async fetchAndDecodeIR(path: string, id: number): Promise<ImpulseResponseData> {
    try {
      // Fetch the audio file
      const response = await fetch(path)
      if (!response.ok) {
        throw new Error(`Failed to fetch impulse response: ${path} (${response.status})`)
      }

      const arrayBuffer = await response.arrayBuffer()

      // Decode audio data
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer)

      return {
        id,
        path,
        audioBuffer,
      }
    } catch (error) {
      throw new Error(`Failed to load impulse response ${path}: ${error}`)
    }
  }

  getLoadedIR(path: string): ImpulseResponseData | undefined {
    const id = hashString(path)
    return this.loadedIRs.get(id)
  }

  clear(): void {
    this.loadedIRs.clear()
    this.loadingPromises.clear()
  }
}

